import { getQueryClient } from '@/context/ReactQueryProvider';
import { wait } from '@/lib/util';

import { fetchVaultList, getVaultInfo } from '../wallet/useQueryAccountVaultList';

import type { BucketSize, Interval, TimeSpan } from './api.types';
import { fetchVaultHistory } from './fetchers/validator.fetch';
import type { ApiError } from './http';
import { poll_closed_vault } from './poll_closed_vault';
import { poll_opened_vault } from './poll_opened_vault';
import { QueryKeys } from './query-keys';
import type { VaultTransaction } from './types';
import { fetchTransactionStatus } from './usePollTransactionStatus';

export { type ApiError, get } from './http';

export const bucketSizeByTimeSpan: Record<TimeSpan, BucketSize> = {
  hour: 'hour',
  day: 'day',
  week: 'day',
  month: 'day',
  year: 'day',
};

const hourInSec = 60 * 60;
export const dayInSec = 24 * hourInSec;

export const rangeByTimeSpan: Record<TimeSpan, number> = {
  hour: 60 * 60,
  day: dayInSec * 2,
  week: dayInSec * 7,
  month: dayInSec * 30,
  year: dayInSec * 365,
};

export const INTERVAL_TO_TIMESPAN: Record<Interval, TimeSpan> = {
  '24h': 'day',
  '7d': 'week',
  '30d': 'month',
  '6m': 'year', // Request 1y data, then filter to 6 months on client
  '1y': 'year',
  ytd: 'year', // Request 1y data, then filter from year start on client
};

// const VAULT_TOKEN_MIN_DELAY = 5_000;
const POLLING_TIMEOUT_MS = 10 * 60_000; // 10 minutes

export async function post<TResult>(url: string, params: unknown): Promise<TResult> {
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(params),
  });
  const contentType = res.headers.get('content-type');
  const isJsonResponse = contentType?.includes('application/json');

  if (!res.ok) {
    let result: Record<string, unknown> | string;
    let errorMessage = `POST request failed: ${res.status} ${res.statusText}`;

    try {
      if (isJsonResponse) {
        result = (await res.json()) as Record<string, unknown>;
        errorMessage = (result.error as string) || errorMessage;
      } else {
        result = await res.text(); // fallback
      }
    } catch {
      result = '';
    }

    const error = new Error(errorMessage) as ApiError;
    error.status = res.status;
    error.url = url;

    if (typeof result === 'object') {
      error.data = result;
    } else {
      error.body = result;
    }

    throw error;
  }
  let result: Record<string, unknown> | string;
  if (isJsonResponse) {
    result = await res.json();
  } else {
    result = await res.text(); // fallback
  }

  return result as TResult;
}

// Poll transaction status till its confirmed.
export async function pollTransactionStatus(
  esploraUrl: string,
  txid: string,
  {
    timeoutMs = POLLING_TIMEOUT_MS,
    onTxInMempool,
    onTxNotFound,
    signal,
  }: {
    timeoutMs?: number;
    onTxInMempool?: () => void;
    onTxNotFound?: () => void;
    signal?: AbortSignal;
  } = {},
) {
  const start = Date.now();
  let isInMempool = false;

  while (true) {
    // Check for cancellation at the start of each iteration
    if (signal?.aborted) {
      throw new DOMException('Polling cancelled', 'AbortError');
    }

    try {
      const data = await fetchTransactionStatus(esploraUrl, txid);
      if (data?.txid !== txid || typeof data.status?.confirmed !== 'boolean') throw new Error('Invalid transaction status response');
      const isConfirmed = data.status.confirmed;
      if (!isInMempool && data) {
        isInMempool = true;
        onTxInMempool?.();
      }

      if (isConfirmed) {
        return true;
      }
    } catch (e: unknown) {
      const error = e as ApiError;

      if (error.status === 404) {
        onTxNotFound?.();
      }
    }

    if (Date.now() - start > timeoutMs) {
      throw new Error(`Timeout: Transaction ${txid} not found`);
    }

    await wait(3000, signal);
  }
}

// Poll vault_history till the expected txid is received.
// For vault close, coin_id is null so transaction_id cannot match — instead wait
// until the latest (newest) history entry has action "close".
export async function pollVaultHistory(
  vaultTxId: string,
  txids: string[],
  validatorUrl: string,
  timeoutMs = POLLING_TIMEOUT_MS,
  signal?: AbortSignal,
  options?: { expectClose?: boolean; canReadVaultHistory?: () => boolean },
) {
  const queryClient = getQueryClient();
  const [txid, vault_txid] = txids;
  const expectClose = options?.expectClose === true;
  const start = Date.now();

  while (true) {
    if (signal?.aborted) {
      throw new DOMException('Polling cancelled', 'AbortError');
    }

    // Transaction confirmation can continue when the connected provider no
    // longer supports personal vaults, without fetching their history again.
    if (options?.canReadVaultHistory?.() === false) return;

    const data = await queryClient.fetchQuery({
      queryKey: [QueryKeys.vaultHistory, vaultTxId],
      queryFn: () => fetchVaultHistory(vaultTxId, validatorUrl),
      staleTime: 0,
    });

    if (expectClose) {
      // convertVaultHistory returns oldest-first; the tip is the last entry.
      const latestTx = data.at(-1);
      if (latestTx?.action === 'close') {
        return latestTx;
      }
    } else {
      const transactionFound = data.some(
        (tx: VaultTransaction) => tx.transaction_id === txid || (vault_txid && tx.transaction_id === vault_txid),
      );

      if (transactionFound) {
        return data.find((tx: VaultTransaction) => tx.transaction_id === txid);
      }
    }

    if (Date.now() - start > timeoutMs) {
      throw new Error(expectClose ? `Timeout: Vault close action not found in history` : `Timeout: Transaction ${txid} not found`);
    }

    await wait(3000, signal);
  }
}

// DEPRECATED.
// Poll ord server to check vault profile utxo response to match the latest txid.
export async function pollVaultToken(vaultPubkey: string, txids: string[], timeoutMs = POLLING_TIMEOUT_MS, signal?: AbortSignal) {
  const queryClient = getQueryClient();
  const start = Date.now();
  const [txid, vault_txid] = txids;

  while (true) {
    if (signal?.aborted) {
      throw new DOMException('Polling cancelled', 'AbortError');
    }

    const data = await queryClient.fetchQuery({
      // queryKey: [QueryKeys.vaultCtx, ordUrl],
      // queryFn: () => fetch_vault_profile(ordUrl, { vid: `${vaultTxId}i1` }),
      queryKey: [QueryKeys.accountVaultList, vaultPubkey],
      queryFn: () => fetchVaultList(vaultPubkey),
      staleTime: 0,
    });

    const resultUtxo = data?.vaults?.[0]?.utxo;

    if (!resultUtxo || typeof resultUtxo !== 'string') {
      throw new Error('Invalid vault data: missing or invalid utxo');
    }

    const utxoParts = resultUtxo.split(':');

    if (utxoParts.length < 2) {
      throw new Error('Invalid utxo format: expected "txid:index"');
    }

    const [utxoTxId] = utxoParts;
    const transactionFound = utxoTxId === txid || (vault_txid && utxoTxId === vault_txid);

    if (transactionFound) {
      return true;
    }

    if (Date.now() - start > timeoutMs) {
      throw new Error(`Ord indexer timeout: utxo not found within ${timeoutMs / 1000} seconds.`);
    }

    await wait(5000, signal);
  }
}

// Poll unit_utxo_by_address till the expected txid is received.
// useQueryRuneUtxos
/*
async function pollRuneUtxos(
  runeAddress: string,
  txids: string[],
  validatorUrl: string,
  timeoutMs = POLLING_TIMEOUT_MS,
  signal?: AbortSignal,
) {
  // const [txid, vault_txid] = txids;
  const [txid] = txids;
  console.log(`>>> [pollRuneUtxos] STARTED for txid=${txid} / runeAddress=${runeAddress}`);
  const start = Date.now();

  while (true) {
    if (signal?.aborted) {
      throw new DOMException('Polling cancelled', 'AbortError');
    }

    const data = await fetchWalletAssets(runeAddress, validatorUrl);

    const utxo = data.find((utxo: AssetAccount) => utxo.coin_id === txid);

    if (utxo) {
      console.log(`>>> Unit UTXO Found. (3) Vault data synced.`, utxo);

      return utxo;
    }

    if (Date.now() - start > timeoutMs) {
      throw new Error(`Timeout: unit UTXO for ${txid} not found`);
    }

    await wait(3000, signal);
  }
}
*/

export async function syncUtxoSources(
  params: {
    vaultPubkey: string;
    txids: string[];
    validatorUrl: string;
    runeAddress?: string;
    expectClose?: boolean;
    expectOpen?: boolean;
    vaultRootTxid?: string;
    canReadVaultHistory?: () => boolean;
  },
  signal?: AbortSignal,
) {
  // const start = Date.now();
  const { vaultPubkey, txids, validatorUrl, expectClose, expectOpen, vaultRootTxid, canReadVaultHistory } = params;
  if (canReadVaultHistory?.() === false) return true;
  if (expectOpen) {
    const root = txids[1];
    if (!root) throw new Error('Opening vault transaction ID is unavailable');
    await poll_opened_vault(root, vaultPubkey, validatorUrl, signal, POLLING_TIMEOUT_MS, canReadVaultHistory);
    return true;
  }
  const vaultTxId = vaultRootTxid || getVaultInfo(vaultPubkey, validatorUrl)?.vaultTxId;
  if (expectClose) {
    if (!vaultTxId) throw new Error('Closing vault identity is unavailable');
    await poll_closed_vault(vaultTxId, vaultPubkey, validatorUrl, signal, POLLING_TIMEOUT_MS, canReadVaultHistory);
    return true;
  }

  if (vaultTxId) {
    await pollVaultHistory(vaultTxId, txids, validatorUrl, POLLING_TIMEOUT_MS, signal, { expectClose, canReadVaultHistory });
  } else {
  }

  // todo: check with v3 if this is needed.
  /*
  if (signal?.aborted) {
    throw new DOMException('Sync cancelled', 'AbortError');
  }

  // Give multiple validators extra time to sync.
  await wait(5000, signal);

  // Wait at least 10s before polling ord:
  const past = Date.now() - start;

  if (VAULT_TOKEN_MIN_DELAY - past > 0) {
    await wait(VAULT_TOKEN_MIN_DELAY - past, signal);
  }

  await pollVaultToken(vaultPubkey, txids, POLLING_TIMEOUT_MS, signal);

  if (runeAddress) {
    return pollRuneUtxos(runeAddress, txids, POLLING_TIMEOUT_MS, signal);
  }
    */

  return true;
}

/**
 * Polls tx status and syncs utxo sources:
 * - sync Vault history
 * - sync Vault Token data
 * - sync rune utxos
 */
const SYNC_UTXO = true;
export async function pollTxStatusAndSync(params: {
  shouldSync: boolean;
  txids: string[];
  esploraUrl: string; // to get tx mempool status (confirmed)
  validatorUrl: string;
  vaultPubkey: string; // sync Vault Token data
  runeAddress?: string; // sync rune utxos
  expectClose?: boolean;
  expectOpen?: boolean;
  vaultRootTxid?: string;
  onTxInMempool?: () => void;
  onTxNotFound?: () => void;
  signal?: AbortSignal;
  canReadVaultHistory?: () => boolean;
}) {
  await pollTransactionStatus(params.esploraUrl, params.txids[0], {
    onTxInMempool: params.onTxInMempool,
    onTxNotFound: params.onTxNotFound,
    signal: params.signal,
  });

  // An opening has a mint transaction and a separate vault transaction.
  // Both must confirm before the validator's indexed state can complete it.
  if (params.expectOpen && params.txids[1]) {
    await pollTransactionStatus(params.esploraUrl, params.txids[1], { signal: params.signal });
  }

  if (params.shouldSync && SYNC_UTXO) {
    return syncUtxoSources(params, params.signal);
  } else {
    return true;
  }
}
